import date from '@dsee/util/date.js';
import DialogMixin from '../../dialog.mixin.js';
import { equalDates, getTime, scalarEquals, timestampVectorEquals, toInnerState, vectorEquals } from '../../util.js';
import { valueDateValidator } from '../../validation.js';
import Picker from '../components/Picker.vue';


const tokens = {
    '#': '0-9',
    'u': '0-1',
    '2': '2',
    't': '0-3',
    'm': '0-5',
};

const scalarMask = [
    '##.##.####',
];

const scalarTimeMask = [
    '##.##.#### u#:m#',
    '##.##.#### 2t:m#',
];

const vectorMask = [
    '##.##.#### - ##.##.####',
];

const vectorTimeMask = [
    '##.##.#### u#:m# - ##.##.#### u#:m#',
    '##.##.#### u#:m# - ##.##.#### 2t:m#',
    '##.##.#### 2t:m# - ##.##.#### u#:m#',
    '##.##.#### 2t:m# - ##.##.#### 2t:m#',
];


export default {
    mixins: [ DialogMixin ],
    props: {
        value: {
            // Date | Array<Date>
            validator: valueDateValidator,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        dateDisabled: {
            type: Function,
            default: (it) => false,
        },
        minDate: {
            type: Date,
            default: () => new Date('1900-01-01'),
        },
        maxDate: {
            type: Date,
            default: () => new Date('2300-01-01'),
        },
        selectRange: {
            type: Boolean,
            default: false,
        },
        timePicker: {
            type: Boolean,
            default: false,
        },
        dualPicker: {
            type: Boolean,
            default: false,
        },
        dataType: {
            type: String,
            default: 'date',
            validator(value) {
                return [ 'date', 'number' ].includes(value);
            },
        },
    },
    components: {
        Picker,
    },
    computed: {
        range() {
            return this.dualPicker || this.selectRange;
        },
        chooseTime1() {
            return this.picker1 === 'day' && this.timePicker;
        },
        chooseTime2() {
            return this.picker2 === 'day' && this.timePicker;
        },
        masks() {
            if (this.range) {
                if (this.timePicker) {
                    return vectorTimeMask;
                } else {
                    return vectorMask;
                }
            } else {
                if (this.timePicker) {
                    return scalarTimeMask;
                } else {
                    return scalarMask;
                }
            }
        },
        maskLength() {
            return this.masks[0].length;
        },
        tokens() {
            return tokens;
        },
        dateFormat() {
            return this.timePicker
                ? 'dd.MM.yyyy HH:mm'
                : 'dd.MM.yyyy';
        },
        dateRegexp() {
            return this.timePicker
                ? /^(\d+)\.(\d+)\.(\d+)\s+(\d+):(\d+)$/
                : /^(\d+)\.(\d+)\.(\d+)$/;
        },
        timestamp() {
            return this.dataType === 'number';
        },
    },
    data() {
        return {
            opened: false,

            hoverDate: null,
            hoverForward: null,

            display: null,
            selected: null,
            lastValue: null,

            picker1: 'day',
            picker2: 'day',

            time1: null,
            time2: null,
        };
    },
    created() {
        this.setState(this.value, 'created');

        this.$options.unwatch = this.$watch((vm) => [
            vm.selected,
            vm.time1,
            vm.time2,
        ], () => {
            this.updateDisplay();
            this.updateResult();
        });

        this.updateDisplay();
    },
    beforeDestroy() {
        this.$options.unwatch();
    },
    watch: {
        value(value) {
            if (this.timestamp) {
                if (this.range) {
                    if (timestampVectorEquals(this.lastValue, value))
                        return;
                } else {
                    if (this.lastValue === value)
                        return;
                }
            } else {
                if (this.range) {
                    if (vectorEquals(this.lastValue, value))
                        return;
                } else {
                    if (scalarEquals(this.lastValue, value))
                        return;
                }
            }

            this.setState(value, 'watch');
        },
    },
    methods: {
        open() {
            if (this.opened || this.disabled)
                return;

            this.opened = true;
        },
        close() {
            this.opened = false;
        },
        onHoverDate(date) {
            this.hoverDate = null;
            this.hoverForward = null;

            if (date == null || date.disabled || !this.range || !this.selected)
                return;

            if (this.selected[0] && this.selected[1])
                return;

            const start = this.selected.find(it => it != null);
            if (!start)
                return;

            this.hoverDate = date.date;

            if (equalDates(start, date.date)) {
                this.hoverForward = null;
                return;
            }

            this.hoverForward = start < date.date;
        },
        setState(value) {
            const temp = toInnerState(value);

            this.selected = temp;

            this.time1 = getTime(temp, 0);
            this.time2 = getTime(temp, 1);
        },
        onInput(text) {
            const fullMask = text.length === this.maskLength || text.length === 0;

            if (!fullMask)
                return;

            const value = this.parseInputValue(text);

            this.setState(value);

            this.lastValue = value;
            this.$emit('input', value);
        },
        parseInputValue(value) {
            if (!value)
                return null;

            if (!this.range)
                return this.parseDateTime(value);

            return value.split(/[-]/)
                        .map(this.parseDateTime);
        },
        parseDateTime(value) {
            if (!value)
                return null;

            const part = value.trim().match(this.dateRegexp);

            if (!part)
                return null;

            return new Date(
                part[3] | 0,
                (part[2] | 0) - 1,
                part[1] | 0,
                part[4] | 0,
                part[5] | 0,
                0,
            );
        },
        updateDisplay() {
            this.display = this.getDisplayValue();
        },
        getDisplayValue() {
            const temp = this.getResultValue();

            if (!temp)
                return null;

            if (!this.range)
                return this.formatDate(temp);

            return temp
                .map(this.formatDate)
                .filter(it => !!it)
                .join(' - ');
        },
        formatDate(temp) {
            if (temp == null)
                return null;

            return date(temp, this.dateFormat);
        },
        updateResult() {
            if (!this.isValidValue())
                return;

            const value = this.getResultValue();
            this.lastValue = value;

            this.$emit('input', value);
        },
        getResultValue() {
            if (this.selected == null)
                return null;

            if (this.timestamp) {
                if (!this.range)
                    return this.getTimestamp(this.selected, this.time1);

                return [
                    this.getTimestamp(this.selected[0], this.time1),
                    this.getTimestamp(this.selected[1], this.time2),
                ];
            } else {
                if (!this.range)
                    return this.getDateTime(this.selected, this.time1);

                return [
                    this.getDateTime(this.selected[0], this.time1),
                    this.getDateTime(this.selected[1], this.time2),
                ];
            }
        },
        getDateTime(origin, time) {
            if (!origin)
                return null;

            origin = new Date(origin);

            if (this.timePicker && time) {
                origin.setHours(0, time, 0, 0);
            } else {
                origin.setHours(0, 0, 0, 0);
            }

            return origin;
        },
        getTimestamp(origin, time) {
            const date = this.getDateTime(origin, time);
            if (date == null)
                return null;

            return date.getTime();
        },
        isValidValue() {
            if (this.selected == null)
                return true;

            if (!this.range)
                return this.isValidDate(this.selected, this.time1);

            return this.isValidDate(this.selected[0], this.time1)
                && this.isValidDate(this.selected[1], this.time2);
        },
        isValidDate(origin, time) {
            if (origin == null)
                return true;

            if (this.timePicker && (time == null || Number.isNaN(time)))
                return false;

            return true;
        },
    },
};
